import type { ReactNode } from 'react';
import { formatMoney, type CurrencyCode } from '@nest-recruit/shared';
import { EMPTY } from '../../../lib/format';
import styles from './SalaryBand.module.css';

/**
 * A position's salary band, in its own currency.
 *
 * **The masking is not done here.** §L23's field rules are enforced on the server,
 * in the serialisation layer (non-negotiable #5) — a viewer who may not see the
 * band receives `null` plus a `restrictedFields` marker, and this component simply
 * renders what arrived. That is what makes the em dash appear on the list, on the
 * detail screen and in the export without any of the three knowing the rule.
 *
 * The distinction it *does* draw is between "hidden from you" and "not set". Both
 * render as an em dash (06-qol §11), but only the first says so on hover — a blank
 * that could mean either is the thing that gets a number disputed.
 */

export interface SalaryBandProps {
  readonly position: {
    readonly salaryBandMinMinor: string | null;
    readonly salaryBandMaxMinor: string | null;
    readonly salaryCurrency: string | null;
    readonly restrictedFields?: readonly string[];
  };
}

export function SalaryBand({ position }: SalaryBandProps): ReactNode {
  const isRestricted = position.restrictedFields?.includes('salaryBandMinMinor') === true;

  if (isRestricted) {
    return (
      <span className={styles.restricted} title="You don't have access to salary bands">
        {EMPTY}
      </span>
    );
  }

  const { salaryBandMinMinor: min, salaryBandMaxMinor: max, salaryCurrency } = position;
  if (salaryCurrency === null || (min === null && max === null)) {
    return <span className={styles.band}>{EMPTY}</span>;
  }

  const currency = salaryCurrency as CurrencyCode;
  return (
    <span className={styles.band}>
      {min === null ? EMPTY : formatMoney({ amountMinor: BigInt(min), currency })}
      <span className={styles.dash} aria-hidden>
        –
      </span>
      {max === null ? EMPTY : formatMoney({ amountMinor: BigInt(max), currency })}
    </span>
  );
}
